import { Spinner } from '@blueprintjs/core';
import { DEMO_CONNECTION_INDEX } from 'portal-sdk';
import * as React from 'react';
import { usePortalConnections, usePortalUser } from 'vet-bones/utils/portal';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { ROUTE } from 'src/constants/routes';
import { ProjectEndpointDialog } from 'src/containers/ProjectEndpointDialog/ProjectEndpointDialog';
import { projectSelector } from 'src/selectors/project';
import { useCloudStorageBackupProjects } from 'src/utils/hooks/useCloudStorageBackupProjects';
import { useCloudStorageProject } from 'src/utils/hooks/useCloudStorageProject';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useNavigate } from 'src/utils/hooks/useNavigate';
import { usePortalConnectionsWithDemo } from 'src/utils/hooks/usePortalConnectionsWithDemo';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { redirectToPortalDashboard } from 'src/utils/redirectToPortalDashboard';

type ProjectRedirectWrapperProps = {
  children?: React.ReactNode;
};

export const ProjectRedirectWrapper: React.VFC<ProjectRedirectWrapperProps> = ({
  children,
}) => {
  const { projectId } = useRouterParams();
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();

  const portalUser = usePortalUser();
  const connections = usePortalConnections();
  const portalConnections = usePortalConnectionsWithDemo();
  const defaultConnection = useDefaultPortalConnection();

  const { data: cloudProject, isLoading } = useCloudStorageProject(projectId);
  useCloudStorageBackupProjects();

  const project = useReduxSelector(projectSelector(projectId));
  const hasProject = project && !project.isRemoved;
  const projectConnectionId = project?.connectionId;
  const projectConnection = portalConnections?.find(
    (c) => c.id === projectConnectionId
  );

  const missingProjectDetails = React.useMemo(
    () => ({ id: projectId, name: project?.name }),
    [project?.name, projectId]
  );

  const hasAdditionalConnections = Boolean(
    portalConnections?.find(
      (c) => c.index !== DEMO_CONNECTION_INDEX && c.id !== defaultConnection?.id
    )
  );

  React.useEffect(() => {
    if (cloudProject?.project) {
      dispatch(
        projectsActionCreators.loadedProjectFromCloud(cloudProject.project)
      );
    }
  }, [cloudProject, dispatch]);

  React.useEffect(() => {
    if (isLoading || !cloudProject || !portalUser.data || !connections.data) {
      // ignore if we're loading cloud project, user feature flags, or connections
      return;
    }

    if (!hasProject) {
      if (!cloudProject.project) {
        // project no longer exists both locally and in cloud
        dispatch(
          projectsActionCreators.openedMissingProject(missingProjectDetails)
        );
        navigate(ROUTE.HOME);
      }

      // there's a mismatch between whether the project was removed between
      // local version and cloud version, do not redirect
      return;
    }

    if (projectConnection && defaultConnection) {
      redirectToPortalDashboard(defaultConnection, projectConnection);
    }

    if (!projectConnection && !hasAdditionalConnections && defaultConnection) {
      dispatch(
        projectsActionCreators.updatedProjectConnectionId(
          projectId,
          defaultConnection.id
        )
      );
    }
  }, [
    cloudProject,
    connections,
    defaultConnection,
    dispatch,
    hasAdditionalConnections,
    hasProject,
    isLoading,
    missingProjectDetails,
    navigate,
    projectConnection,
    projectConnectionId,
    projectId,
    portalUser,
  ]);

  if (portalUser.data && connections.data && hasProject) {
    if (!projectConnectionId || !projectConnection) {
      // project does not have a valid connection, open the endpoint dialog
      return <ProjectEndpointDialog />;
    }

    if (projectConnectionId === defaultConnection?.id) {
      // project connection is valid and matches the curernt connection
      // show the page contents
      return <>{children}</>;
    }
  }

  // othrwise, show the loading indicator
  // this will occur when the project data, user features, connections, are loading
  // or after a project endpoint has been confirmed and waiting for a redirect
  return <Spinner />;
};
